import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateUtilsService {
  
  /**
   * Converts a date to local timezone and formats it
   * @param value - Date value (string, Date, or number)
   * @param format - Format string (e.g., 'dd/MM/yyyy', 'dd-MM-yyyy HH:mm')
   * @returns Formatted date string in local timezone
   */
  parseToLocalDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    let date: Date;

    if (typeof value === 'string') {
      if (this.isUtcDate(value)) {
        date = new Date(value);
      } else {
        date = new Date(`${value}Z`);
      }
    } else if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else {
      return null;
    }

    return Number.isNaN(date.getTime()) ? null : date;
  }

  formatDateToLocal(value: any, format: string = 'dd/MM/yyyy'): string {
    const date = this.parseToLocalDate(value);
    if (!date) {
      return '';
    }
    
    // Convert to local timezone and format
    const options: Intl.DateTimeFormatOptions = {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    // Parse the format string and convert to Intl options
    this.parseFormatString(format, options);
    
    let formattedDate = date.toLocaleDateString('en-GB', options);
    
    // Handle custom AM/PM format
    if (format.includes('a') && options.hour12) {
      formattedDate = this.formatAmPm(formattedDate);
    }
    
    return formattedDate;
  }
  
  /**
   * Converts a date to local timezone and formats it with time
   * @param value - Date value
   * @param format - Format string
   * @returns Formatted date and time string in local timezone
   */
  formatDateTimeToLocal(value: any, format: string = 'dd/MM/yyyy HH:mm'): string {
    const date = this.parseToLocalDate(value);
    if (!date) {
      return '';
    }
    
    const options: Intl.DateTimeFormatOptions = {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    this.parseFormatString(format, options);
    
    let formattedDate = date.toLocaleString('en-GB', options);
    
    // Handle custom AM/PM format
    if (format.includes('a') && options.hour12) {
      formattedDate = this.formatAmPm(formattedDate);
    }
    
    return formattedDate;
  }
  
  /**
   * Parses format string and sets appropriate Intl options
   * @param format - Format string
   * @param options - Intl options object to modify
   */
  private parseFormatString(format: string, options: Intl.DateTimeFormatOptions): void {
    if (format.includes('dd')) {
      options.day = '2-digit';
    }
    if (format.includes('MM')) {
      options.month = '2-digit';
    }
    if (format.includes('yyyy')) {
      options.year = 'numeric';
    }
    if (format.includes('HH')) {
      options.hour = '2-digit';
      options.hour12 = false;
    }
    if (format.includes('hh')) {
      options.hour = '2-digit';
      options.hour12 = true;
    }
    if (format.includes('mm')) {
      options.minute = '2-digit';
    }
    if (format.includes('a')) {
      options.hour12 = true;
    }
  }
  
  /**
   * Gets the current timezone of the client
   * @returns Timezone string (e.g., 'America/New_York', 'Europe/London')
   */
  getClientTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  
  /**
   * Checks if a date string is in UTC format
   * @param dateString - Date string to check
   * @returns True if the date is in UTC format
   */
  isUtcDate(dateString: string): boolean {
    return dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10);
  }
  
  /**
   * Formats AM/PM to A.M/P.M format
   * @param formattedDate - Date string with AM/PM
   * @returns Date string with A.M/P.M format
   */
  private formatAmPm(formattedDate: string): string {
    return formattedDate
      .replace(/\bam\b/gi, 'A.M')
      .replace(/\bpm\b/gi, 'P.M');
  }
}
